import { Component, Host, Listen, Prop, h } from '@stencil/core';
import { Unsubscribe, store } from '@stencil/redux';
import { MessageVariant } from '@sfc/types';
import {
  ContinueResponse,
  GetChallengeResponse,
  SignInitResponse,
} from '../../class/class';
import { Constants, Language } from '../../enums/const-enums';
import { clearOverlay } from '../../redux/actions/overlay-actions';
import { OverlayState } from '../../redux/reducers/overlay-reducer';
import { RootState, initialStore } from '../../redux/store';
import { SignCancel } from '../../service/sign-cancel';
import { isDarkMode } from '../../utils/theming';
import {
  getCookieValue,
  handleContinueResponse,
  handleMethodAndChallengeDataWithoutMalwareCheck,
  logd,
} from '../../utils/utils';
import {
  CodeTranslations,
  rassOverlayTranslations,
} from './i18n/rass-overlay.i18n';

@Component({
  tag: 'rass-overlay',
  styleUrl: 'rass-overlay.scss',
  shadow: true,
})
export class RassOverlay implements OverlayState {
  @Prop() assetsPath = '/s-t-a-t-i-c/msp/authentication/v2/vrs_4820747/assets/';
  @Prop() sfcPath: string;
  @Prop() language: Language;
  @Prop() buttonLabel: string;
  @Prop() logoutButtonLabel: string;
  @Prop({ mutable: true }) code: string;
  @Prop() malwareLimitSigningButtonLabel: string;
  @Prop() resp: SignInitResponse | GetChallengeResponse;
  unsubscribe!: Unsubscribe;

  @Prop({ mutable: true }) active = false;
  @Prop({ mutable: true }) content: string;
  @Prop({ mutable: true }) messageTitle: string;
  @Prop({ mutable: true }) message: string;
  @Prop({ mutable: true }) type: MessageVariant;
  @Prop({ mutable: true }) url: string;
  malwareLimitSigningWarningCodes = ['00801', '00802', '00803'];
  malwareLimitSigningErrorCodes = ['860', '861', '862'];
  malwareLimitSigningCodes = this.malwareLimitSigningWarningCodes.concat(
    this.malwareLimitSigningErrorCodes,
  );
  clearOverlay!: typeof clearOverlay;

  componentWillLoad(): void {
    if (this.code) {
      this.populateMessageAndButtonLabel();
      this.active = true;
      this.type =
        this.code === '00706' || this.code === '00715' ? undefined : 'error';
      this.url = this.url || 'https://www.rabobank.nl';
    }
    store.setStore(initialStore);
    store.mapDispatchToProps(this, {
      clearOverlay,
    });
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      const {
        overlay: {
          active,
          content,
          message,
          type,
          url,
          messageTitle,
          code,
          resp,
        },
      } = state;
      this.processStateForMalwareLimitSigning(code, resp);
      return {
        active: active || this.active,
        content: content || this.content,
        messageTitle: messageTitle || this.messageTitle,
        message: message || this.message,
        type: type || this.type,
        url: url || this.url,
        code: code || this.code,
        resp: resp || this.resp,
      };
    });
  }

  processStateForMalwareLimitSigning(
    code: string,
    resp: SignInitResponse | GetChallengeResponse,
  ): void {
    if (code && !this.code && this.isMalwareLimitSigningCode(code)) {
      this.code = code;
      this.resp = resp;
      this.populateMessageAndButtonLabel();
    }
  }

  isMalwareLimitSigningCode(code: string): boolean {
    return this.malwareLimitSigningCodes.some((c) => c === code);
  }

  isMalwareLimitSigningWarningCode(code: string): boolean {
    return this.malwareLimitSigningWarningCodes.some((c) => c === code);
  }

  populateMessageAndButtonLabel(): void {
    const translation = rassOverlayTranslations[this.selectedlanguage];
    const codeMapping: CodeTranslations = translation.codes[this.code] || {};
    const defaults: CodeTranslations = translation.default;
    this.messageTitle = codeMapping.messageTitle;
    this.message = `${codeMapping.message || defaults.message} (${this.code})`;
    this.buttonLabel = codeMapping.buttonLabel || defaults.buttonLabel;
    this.logoutButtonLabel =
      codeMapping.logoutButtonLabel || defaults.logoutButtonLabel;
    this.malwareLimitSigningButtonLabel =
      translation.default.malwareLimitSigningButtonLabel;
  }

  @Listen('overlayStateChange', {
    target: 'document',
  })
  overlayStateChangeHandler(event: CustomEvent): void {
    Object.assign(this, { active: true }, event.detail);
    const bodyElement: HTMLElement = document.querySelector('body');
    if (
      this.active === true &&
      bodyElement.className.includes('isOverlayActive') === false
    ) {
      bodyElement.classList.add('isOverlayActive');
    }
  }

  handleClickContinueBtn(): void {
    handleContinueResponse(
      new ContinueResponse({
        continue_url: this.url,
        continue_content: this.content,
      }),
      'failure',
    );
  }

  handleMalwareLimitSigning(): void {
    if (this.isMalwareLimitSigningWarningCode(this.code)) {
      this.active = false;
      this.clearOverlay();
      handleMethodAndChallengeDataWithoutMalwareCheck(this.resp);
    } else {
      this.cancelSigning();
    }
  }

  cancelSigning(): void {
    new SignCancel()
      .doSignCancel()
      .then((resp) => {
        handleContinueResponse(resp, 'canceled');
      })
      .catch((err: Error) => logd(() => console.error(err)));
  }

  get isMalwareLimitSigning(): boolean {
    return this.isMalwareLimitSigningCode(this.code);
  }

  get showContinueButton(): boolean {
    return !!this.url || !!this.content;
  }

  get sfcStylePath(): string {
    if (this.sfcPath) {
      return `${this.sfcPath}style/sfc-style.css`;
    } else {
      return `${this.assetsPath}css/sfc-style.css`;
    }
  }

  get messageHtml(): void {
    return (
      <div>
        {this.type ? (
          <sfc-notification type={this.type} innerHTML={this.message} />
        ) : (
          <div class="sfc-margin-block-end-150">
            {this.messageTitle ? (
              <h1 class="sfc-heading-1 sfc-font-text-align-center">
                {this.messageTitle}
              </h1>
            ) : (
              ''
            )}
            <p class="sfc-paragraph sfc-margin-block-end-0 sfc-font-text-align-center">
              {this.message}
            </p>
          </div>
        )}
      </div>
    ) as void;
  }

  get selectedlanguage(): Language {
    return (
      this.language ||
      (getCookieValue(Constants.LANGUAGE_COOKIE_NAME) as Language) ||
      Language.EN
    );
  }

  get imageThemeFragment(): string {
    return isDarkMode() ? 'dm' : 'lm';
  }

  get logoutUrl(): string {
    if (this.url) {
      const tempUrl = this.url.startsWith(location.protocol)
        ? new URL(this.url)
        : new URL(this.url, location.origin);
      return `${this.url.replace(tempUrl.search, '')}/logout.htm`;
    }
    return '';
  }

  render(): void {
    return (
      <Host class={this.active === true ? 'isActive' : 'isInactive'}>
        <style>@import url({`'${this.sfcStylePath}'`});</style>
        <div class="sfc-container body-width" role="form">
          <div class="section-logo sfc-row sfc-justify-content-center sfc-padding-block-200">
            <div class="sfc-col-12">
              <img
                class="logo"
                src={`${this.assetsPath}images/rabobank-${this.imageThemeFragment}.svg`}
                alt="Rabobank"
              />
            </div>
          </div>
          <div class="section-pressure-cooker sfc-row sfc-justify-content-center sfc-margin-block-end-150">
            {this.type === 'error' && !this.isMalwareLimitSigning && (
              <div class="pressure-cooker">
                <sfc-illustration
                  name="rds-cooking-pan"
                  id="pressure-cooker-img"
                />
              </div>
            )}
            {!this.type && (
              <div class="not-found">
                <sfc-illustration name="rds-tree-with-cat" id="not-found-img" />
              </div>
            )}
            {this.isMalwareLimitSigning && (
              <div class="smartphone-criminal">
                <sfc-illustration
                  name="rds-smartphone-criminal"
                  id="smartphone-criminals-img"
                />
              </div>
            )}
          </div>
          <div class="section-notification sfc-row sfc-justify-content-center sfc-margin-block-end-50">
            <div class="sfc-col-12">{this.message ? this.messageHtml : ''}</div>
          </div>
          <div
            class={
              this.isMalwareLimitSigning
                ? 'sfc-display-none'
                : 'section-button sfc-row sfc-justify-content-center sfc-margin-block-end-150'
            }
          >
            <div class="sfc-col-12 sfc-display-flex sfc-flex-column sfc-align-items-center">
              <sfc-button
                id="continue-btn"
                onSfcClick={(): void => this.handleClickContinueBtn()}
              >
                {this.buttonLabel}
              </sfc-button>
            </div>
          </div>
          <div
            class={
              this.isMalwareLimitSigning
                ? 'section-button sfc-row sfc-justify-content-center sfc-margin-block-end-150'
                : 'sfc-display-none'
            }
          >
            <div class="sfc-col-12 sfc-display-flex sfc-flex-column sfc-align-items-center">
              <sfc-button
                id="understood-btn"
                onSfcClick={(): void => this.handleMalwareLimitSigning()}
              >
                {this.malwareLimitSigningButtonLabel}
              </sfc-button>
            </div>
          </div>

          <div
            class={
              this.code === '00715'
                ? 'section-button sfc-row sfc-justify-content-center sfc-margin-block-end-150'
                : 'sfc-display-none'
            }
          >
            <div class="sfc-col-12 sfc-display-flex sfc-flex-column sfc-align-items-center">
              <a class="sfc-link" href={this.logoutUrl} id="logout-btn">
                {this.logoutButtonLabel}
              </a>
            </div>
          </div>
        </div>
      </Host>
    ) as void;
  }
}
